<script setup>
import { computed } from 'vue'

const props = defineProps({
    team: {
        type: Object,
        required: true,
    },
    expanded: {
        type: Boolean,
        default: false,
    },
    actionTooltipText: {
        type: String,
        default: '',
    },
})

defineEmits(['toggle', 'view', 'edit', 'delete'])

const visibleCollaborators = computed(() => props.team.colaboradores?.slice(0, 3) ?? [])
</script>

<template>
    <article class="rhdigital-team-card" :class="{ 'rhdigital-team-card--expanded': expanded }">
        <header class="rhdigital-team-card__header">
            <div class="rhdigital-team-card__main">
                <span class="rhdigital-team-card__icon material-symbols-outlined">groups</span>

                <div>
                    <div class="rhdigital-team-card__title-row">
                        <h2>{{ team.nome }}</h2>
                        <span>{{ $t('components.rhdigital.list.teams.members', { count: team.colaboradores?.length ?? 0 }) }}</span>
                    </div>
                    <p>{{ team.departamento }}</p>
                </div>
            </div>

            <div class="rhdigital-team-card__side">
                <div class="rhdigital-team-card__avatars" aria-hidden="true">
                    <span
                        v-for="collaborator in visibleCollaborators"
                        :key="collaborator.id"
                        :style="{ backgroundColor: collaborator.cor }"
                    >
                        {{ collaborator.iniciais }}
                    </span>
                </div>

                <div class="rhdigital-team-card__actions">
                    <button
                        type="button"
                        :aria-label="$t('common.labels.view')"
                        v-tooltip="actionTooltipText || false"
                        @click.stop="!actionTooltipText && $emit('view', team)"
                    >
                        <i class="material-symbols-outlined">visibility</i>
                    </button>
                    <button
                        type="button"
                        :aria-label="$t('common.labels.edit')"
                        v-tooltip="actionTooltipText || false"
                        @click.stop="!actionTooltipText && $emit('edit', team)"
                    >
                        <i class="material-symbols-outlined text-blue">edit</i>
                    </button>
                    <button
                        type="button"
                        :aria-label="$t('common.labels.delete')"
                        v-tooltip="actionTooltipText || false"
                        @click.stop="!actionTooltipText && $emit('delete', team)"
                    >
                        <i class="material-symbols-outlined text-danger">delete</i>
                    </button>
                </div>

                <button class="rhdigital-team-card__toggle" type="button" @click="$emit('toggle', team.id)">
                    <i class="material-symbols-outlined">
                        {{ expanded ? 'expand_less' : 'expand_more' }}
                    </i>
                </button>
            </div>
        </header>

        <div v-if="expanded" class="rhdigital-team-card__details">
            <div class="rhdigital-team-card__table">
                <div class="rhdigital-team-card__table-head">
                    <span>{{ $t('components.rhdigital.list.fields.colaborador') }}</span>
                    <span>{{ $t('components.rhdigital.list.fields.funcao') }}</span>
                    <span>{{ $t('components.rhdigital.list.fields.email') }}</span>
                    <span>{{ $t('common.labels.status') }}</span>
                </div>

                <div
                    v-for="collaborator in team.colaboradores"
                    :key="collaborator.id"
                    class="rhdigital-team-card__member"
                >
                    <div class="rhdigital-team-card__person">
                        <span :style="{ backgroundColor: collaborator.cor }">
                            {{ collaborator.iniciais }}
                        </span>
                        <strong>{{ collaborator.nome }}</strong>
                    </div>
                    <span>{{ collaborator.funcao }}</span>
                    <span>{{ collaborator.email }}</span>
                    <span>
                        <span class="rhdigital-team-card__status" :class="{ 'rhdigital-team-card__status--inactive': !collaborator.status }">
                            {{ $t('common.labels.' + (collaborator.status ? 'active' : 'inactive')) }}
                        </span>
                    </span>
                </div>
            </div>
        </div>
    </article>
</template>

<style lang="scss" scoped>
.rhdigital-team-card {
    overflow: hidden;
    border: 1px solid #e1e7f2;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 10px 28px rgba(34, 48, 73, 0.04);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &--expanded {
        border-color: #625bf6;
        box-shadow: 0 18px 40px rgba(98, 91, 246, 0.08);
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
        min-height: 86px;
        padding: 20px 24px;
    }

    &__main,
    &__side,
    &__title-row,
    &__actions,
    &__avatars,
    &__person {
        display: flex;
        align-items: center;
    }

    &__main {
        gap: 16px;
        min-width: 0;
    }

    &__icon {
        display: grid;
        place-items: center;
        width: 44px;
        height: 44px;
        flex: 0 0 auto;
        border-radius: 9px;
        background: #f3f0ff;
        color: #625bf6;
        font-size: 22px;
    }

    &__title-row {
        flex-wrap: wrap;
        gap: 10px;

        h2 {
            margin: 0;
            color: #271246;
            font-size: 16px;
            font-weight: 850;
        }

        span {
            padding: 4px 10px;
            border-radius: 999px;
            background: #f1edff;
            color: #625bf6;
            font-size: 13px;
            font-weight: 800;
        }
    }

    p {
        margin: 7px 0 0;
        color: #9ca6c9;
        font-size: 14px;
        font-weight: 500;
    }

    &__side {
        gap: 16px;
        flex: 0 0 auto;
    }

    &__avatars {
        justify-content: flex-end;

        span {
            display: grid;
            place-items: center;
            width: 28px;
            height: 28px;
            margin-left: -7px;
            border: 2px solid #fff;
            border-radius: 50%;
            color: #fff;
            font-size: 10px;
            font-weight: 850;

            &:first-child {
                margin-left: 0;
            }
        }
    }

    &__actions {
        gap: 6px;

        button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            border: 0;
            border-radius: 7px;
            background: transparent;
            cursor: pointer;
        }

        i {
            font-size: 19px;
        }
    }

    &__toggle {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: #9ca6c9;
        cursor: pointer;

        i {
            font-size: 22px;
        }
    }

    &__details {
        border-top: 1px solid #e6ebf4;
        background: #fbfcff;
    }

    &__table {
        min-width: 720px;
    }

    &__table-head,
    &__member {
        display: grid;
        grid-template-columns: minmax(210px, 1.5fr) minmax(140px, 0.9fr) minmax(220px, 1.3fr) 120px;
        align-items: center;
        gap: 24px;
        padding: 0 20px;
    }

    &__table-head {
        height: 42px;
        color: #a3aad0;
        font-size: 12px;
        font-weight: 850;
        letter-spacing: 0.02em;
        text-transform: uppercase;

        span {
            color:#3c3c3c;
        }
    }

    &__member {
        min-height: 63px;
        border-top: 1px solid #e6ebf4;
        color: #3c3c3c;
        font-size: 14px;
    }

    &__person {
        gap: 12px;

        span {
            display: grid;
            place-items: center;
            width: 34px;
            height: 34px;
            flex: 0 0 auto;
            border-radius: 50%;
            color: #fff;
            font-size: 11px;
            font-weight: 850;
        }

        strong {
            color: #271246;
            font-weight: 850;
        }
    }

    &__status {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 4px 10px;
        border-radius: 999px;
        background: #e8fbf5;
        color: #13c28b;
        font-size: 13px;
        font-weight: 800;

        &::before {
            content: '';
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: currentColor;
        }

        &--inactive {
            background: #ffeaf3;
            color: #eb3e8c;
        }
    }
}

.dark-theme {
    .rhdigital-team-card {
        border-color: var(--borderColor);
        background: #0c1427;
        box-shadow: none;

        &--expanded {
            border-color: #625bf6;
        }

        &__icon,
        &__title-row span {
            background: rgba(98, 91, 246, 0.16);
            color: #9d98ff;
        }

        &__title-row h2,
        &__person strong {
            color: var(--whiteColor);
        }

        p,
        &__toggle,
        &__member,
        &__table-head span {
            color: white!important;
        }

        &__avatars span {
            border-color: #0c1427;
        }

        &__details {
            border-top-color: var(--borderColor);
            background: #0a0e19;
        }

        &__table-head {
            color: #7f8da5;
        }

        &__member {
            border-top-color: var(--borderColor);
        }

        &__status {
            background: rgba(19, 194, 139, 0.12);

            &--inactive {
                background: rgba(235, 62, 140, 0.12);
            }
        }
    }
}

@media (max-width: 991px) {
    .rhdigital-team-card {
        &__header {
            align-items: flex-start;
            flex-direction: column;
        }

        &__side {
            justify-content: space-between;
            width: 100%;
        }

        &__details {
            overflow-x: auto;
        }
    }
}

@media (max-width: 575px) {
    .rhdigital-team-card {
        &__header {
            padding: 16px;
        }

        &__main {
            align-items: flex-start;
        }

        &__side {
            gap: 10px;
        }

        &__actions {
            gap: 2px;
        }
    }
}
</style>
